import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { AttachMoney, Calculate, GridOn, Group, Groups, ManageAccounts, ReportProblem, Rule, EmojiEvents } from '@mui/icons-material';
import { UserContext } from '../contexts/userContext';
import { useContext, useEffect } from 'react';
import { Badge, Button, useMediaQuery } from '@mui/material';
import UserProfile from './login/UserProfile';
import { grey } from '@mui/material/colors';
import { getSectionName } from '../utils/utils';
import { useGetAlertas } from '../hooks/alertas';

export const drawerWidth = 280;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return ({
    flexGrow: 1,
    padding: isMobile ? 0 : theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  });
});

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const DrawerFooter = styled('div')(() => ({
  width: drawerWidth,
  position: 'fixed',
  bottom: 0,
  justifyContent: 'center',
  color: grey[600],
}));
export interface PersistentDrawerLeftInterface {
  children: React.ReactNode,
  setCurrentlySelected: React.Dispatch<React.SetStateAction<string | null>>
}

export default function PersistentDrawerLeft(props:PersistentDrawerLeftInterface) {
  const { children, setCurrentlySelected } = props;
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const { user } = useContext(UserContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hayAlerta, faltanCuotas, qty } = useGetAlertas();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const items = JSON.parse(JSON.stringify(user?.roles ?? [])).filter((i:string) =>
    i !== 'editar datos'
  && i !== 'registrar pagos'
  && (i !== 'admi cuotas' || !isMobile)
  && (i !== 'ver ingresos' || !isMobile),
  );

  if (items.includes('asistencia') && !isMobile) items.push('planilla asistencia');

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <Badge invisible={!hayAlerta || !user?.roles?.includes('ver alertas')} badgeContent={qty} color="error">
              <MenuIcon />
            </Badge>
          </IconButton>
          {isMobile ? <img src={process.env.PUBLIC_URL + '/mobilelogo2.png'} style={{ width: 66 }} />
            : <img src={process.env.PUBLIC_URL + '/logo2.png'} style={{ width: 300 }} />}
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <UserProfile />
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {items?.map((item:string) => {
            let color = undefined;
            if (faltanCuotas?.length && item === 'admi cuotas') color = theme.palette.error.main;
            if (hayAlerta && item === 'ver alertas') color = theme.palette.error.main;
            return (
              <ListItem key={item} disablePadding>
                <ListItemButton onClick={() => {
                  setCurrentlySelected(item);
                  setOpen(false);
                }}>
                  <ListItemIcon>
                    {item === 'asistencia'  && <Rule />}
                    {item === 'ver datos'  && <Group />}
                    {item === 'admin usuarios'  && <ManageAccounts />}
                    {item === 'admin grupos'  && <Groups />}
                    {item === 'admin torneos'  && <EmojiEvents />}
                    {item === 'admi cuotas'  && <AttachMoney style={{ color }}/>}
                    {item === 'ver alertas'  &&
                                <Badge invisible={!hayAlerta} badgeContent={qty} color="error">
                                  <ReportProblem style={{ color }}/>
                                </Badge>
                    }
                    {item === 'planilla asistencia'  && <GridOn />}
                    {item === 'ver ingresos'  && <Calculate />}
                  </ListItemIcon>
                  <ListItemText primary={getSectionName(item)} style={{ color }}/>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <DrawerFooter>
          <Button
            onClick={() =>
              window.open(
                'https://www.linkedin.com/in/mart%C3%ADn-folmer-89b454198/',
              )
            }
            size="small"
            variant="text"
            style={{ color: grey[600], fontSize: 12, textTransform: 'none' }}
          >
            {`version: ${process.env.REACT_APP_VERSION} | powered by FolmerSolutions`}
          </Button>
        </DrawerFooter>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}
